/* eslint-disable indent */
import {
  useState,
  useCallback,
  useContext,
  useRef,
  useEffect,
  useMemo,
} from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { EngineCorrectionContext } from '@/providers/EngineCorrectionProvider';
import { IconCircleDashedCheck, IconHandStop } from '@tabler/icons-react';
import Card from '@/components/Card';
import Canvas from '@/components/Custom/Canvas';
import { usePostFileSFTP } from '@/hooks/sftp.hooks';
import Loader from '@/components/Loading/Loader';
import ListElementCar from '@/components/Custom/ListElementCar';
import { IconPlayerTrackNext } from '@tabler/icons-react';
import {
  useUpdateCarProcessedToModerate,
  useUpdateFirestoreImage,
  useUpdateProcessing,
  useUploadProcessingImage,
} from '@/hooks/ai.hooks';
import useReportData from '@/hooks/useReportData';
import { ElementType } from '@/entities/ia';
import { CORRECTION_TYPE, R2_PUBLIC_DOMAIN } from '@/utils/constant.utils';
import { getColorByValue, getFileToUpload, initReport } from '@/utils/ia.utils';
import { FileData } from '@/api/entities/engineCorrection';
import { createFileFromUrl } from '@/utils/file.utils';
import { MODE_DRAW } from '@/utils/constant.utils';
import { TResultImageCoordinatesSchema } from '@/schemas/vehicule-check-result/result-image-coordinates.schema';
import { useGetModerationEquipments } from '@/hooks/equipment.hooks';
import { useGetModerationDamages } from '@/hooks/damage.hooks';
import { useTranslation } from 'react-i18next';
import { TitleSection } from '@/components/Header/Section/TitleSection';
import { TabList } from '@/components/Tab/TabList';
import { TabItem } from '@/components/Tab/TabItem';
import { twMerge } from 'tailwind-merge';
import { RotationIcon } from '@/components/SvgIcons/RotationIcon';
import { ModerationReportCanvas } from '@/components/Custom/ModerationReportCanvas';
import { BackButton } from '@/components/Custom/Button/BackButton';
import { getEquipmentByClassId } from '@/api/equipment/equipment';
import { getDamageByClassId } from '@/api/damage/damage';
import { addImageCacheBuster } from '@/utils/image.utils';
import { NotFound } from '@/components/Message/NotFound';
import { EditEquipmentTab } from '@/type/ui/tab/equipmentTab';
import { Switch } from '@/components/Controls/Switch';
import { useAuth, useUserRole } from '@/hooks/auth.hooks';
import { NOTIFY } from '@/utils/constant.utils';
import { notify } from '@/utils/toast.utils';
import { ValidationTag } from './components/ValidationTag';
import { HumanModerationStatus } from '@/api/entities/moderation';
import { useMediaQuery } from '@mui/material';

const FIREBASE_BASE_URL = 'https://firebasestorage.googleapis.com';

interface EngineCorrectionModerationProps {
  processingId?: string;
  backToLink?: string;
  showOnlyEquipments?: boolean;
  showOnlyDamages?: boolean;
  showOnlyDetections?: boolean;
  showOnlyModerations?: boolean;
  equipmentClassId?: number;
}

export const EngineCorrectionModeration = ({
  processingId,
  backToLink,
  showOnlyEquipments = false,
  showOnlyDamages = false,
  showOnlyDetections = false,
  showOnlyModerations = false,
  equipmentClassId,
}: EngineCorrectionModerationProps) => {
  const { t } = useTranslation('engineCorrection');

  const isNotOnModerationScreen = useMediaQuery('(max-width: 1500px)');

  const { loggedInUser } = useAuth();
  const { isAdmin } = useUserRole();

  const {
    setReportEngineData,
    listElement,
    setListElement,
    listDamage,
    setListDamage,
    dataAnnotation,
    setDataAnnotation,
    dataAnnotationDamage,
    setDataAnnotationDamage,
    currentElement,
    setIsdDeleted,
    setSendLogs,
  } = useContext(EngineCorrectionContext);

  const navigate = useNavigate();
  const { id } = useParams();

  const currentId = processingId || id;

  const [searchParams] = useSearchParams();
  const tab = searchParams.get('tab');

  const engineContinueRef = useRef<boolean>();
  const rotationCountRef = useRef<number>(0);
  const newImageUrlRef = useRef<string | null>(null);
  const transformedAnnotationRef = useRef<TResultImageCoordinatesSchema[]>([]);
  const transformedDamageAnnotationRef = useRef<
    TResultImageCoordinatesSchema[]
  >([]);
  const transformedListElementRef = useRef<ElementType[]>([]);
  const transformedListDamageRef = useRef<ElementType[]>([]);

  const {
    dataCar,
    isPending: isPendingDataCar,
    carProcessedNext,
    generateReportData,
  } = useReportData({
    id: currentId,
    showOnlyDetections,
    showOnlyModerations,
    equipmentClassId,
  });

  const [showElement, setShowElement] = useState<boolean>(true);
  const [showDamage, setShowDamage] = useState<boolean>(false);
  const [correctionNumber, setCorrectionNumber] = useState<{
    correctionElNb?: number;
    correctionDmgNb?: number;
  }>({ correctionElNb: 0, correctionDmgNb: 0 });
  const correctionNumberRef = useRef(correctionNumber);
  const [rotationAngle, setRotationAngle] = useState<number>(0);
  const [temporaryImageUrl, setTemporaryImageUrl] = useState<string | null>(
    null,
  );
  const [isLoadingRotatedUrl, setIsLoadingRotatedUrl] =
    useState<boolean>(false);

  const [isLoadingDataByClassId, setIsLoadingDataByClassId] =
    useState<boolean>(false);

  // State for rectangle-based elements (used when withOldElementRectangles is true)
  const [, setListElementWithRectangles] = useState<ElementType[]>([]);
  const [, setDataAnnotationWithRectangles] = useState<
    TResultImageCoordinatesSchema[]
  >([]);

  const [moderationVerifiedByUserId, setModerationVerifiedByUserId] =
    useState<string>();

  const disableModeration = showElement && dataCar?.withOldElementRectangles;

  // Memoize the image URL to prevent reloading on tab switches
  const stableImageUrl = useMemo(() => {
    if (!dataCar?.imageUrl) return null;
    return addImageCacheBuster(dataCar?.imageUrl);
  }, [dataCar?.imageUrl]);

  const { equipmentsSelectOptions } = useGetModerationEquipments();
  const { allDamagesSelectOptions } = useGetModerationDamages();

  const { mutate: updateFirestoreImage, isPending: isUpdatingFirestoreImage } =
    useUpdateFirestoreImage({ t });

  const { mutate: updateProcessing, isPending: isPendingUpdateProcessing } =
    useUpdateProcessing({
      t,
      handleSuccess: () => {
        updateFirestoreImage({
          previousImageUrl:
            rotationCountRef.current === 1
              ? dataCar?.imageUrl
              : newImageUrlRef.current,
          imageUrl: newImageUrlRef.current,
        });
      },
    });

  const {
    mutate: updateModerationValidation,
    isPending: isPendingUpdateModerationValidation,
  } = useUpdateProcessing({
    t,
    handleSuccess: () => {
      notify(t('moderationVerificationUpdated'), NOTIFY.SUCCESS);
    },
  });

  const { mutate: uploadRotatedImage, isPending: isUploadingRotatedImage } =
    useUploadProcessingImage({
      handleSuccess: (data: {
        data: { files: Array<{ filename: string; path: string }> };
      }) => {
        // Get the first uploaded file (since we're only uploading one rotated image)
        const uploadedFile = data.data.files[0];
        newImageUrlRef.current = `${R2_PUBLIC_DOMAIN}/${uploadedFile.path}`;

        // Preload the new image to ensure it's ready before switching
        const newImage = new Image();
        newImage.onload = () => {
          // Only switch to new URL and clean up temporary URL after new image has loaded
          if (temporaryImageUrl) {
            URL.revokeObjectURL(temporaryImageUrl);
          }
          setTemporaryImageUrl(addImageCacheBuster(newImageUrlRef.current));
          setIsLoadingRotatedUrl(false);
        };
        newImage.onerror = () => {
          // If new image fails to load, keep the temporary URL and stop loading
          console.error('Failed to load new rotated image');
          setIsLoadingRotatedUrl(false);
        };

        newImage.src = addImageCacheBuster(newImageUrlRef.current);

        const fileName = uploadedFile.filename?.replace('.jpg', '');

        updateProcessing({
          id: dataCar?.id,
          publicUrl: newImageUrlRef.current,
          fileName: newImageUrlRef.current,
          fileNameElementsDetected: fileName,
          fileNameDamagesDetected: fileName,
        });
      },
    });

  const { mutate: updateProcess, isPending: isPendingUpdateProcess } =
    useUpdateCarProcessedToModerate(() => {
      const nextId = carProcessedNext?.data?.id;
      setListElement([]);
      setListDamage([]);
      setDataAnnotation([]);
      setDataAnnotationDamage([]);
      if (engineContinueRef.current == true) {
        navigate(`/engine-correction/${nextId}`);
      } else if (engineContinueRef.current == false) {
        if (processingId) {
          navigate(backToLink);
        } else {
          navigate('/moteur-de-correction-liste');
        }
      }
    });

  const { mutate: uploadFileToSFTP, isPending: isPendingUploadFileToSFTP } =
    usePostFileSFTP();

  const isLoadingModeration =
    isPendingDataCar ||
    isPendingUploadFileToSFTP ||
    isPendingUpdateProcess ||
    isPendingUpdateModerationValidation;

  const isLoadingRotation =
    isUploadingRotatedImage ||
    isPendingUpdateProcessing ||
    isUpdatingFirestoreImage ||
    isLoadingRotatedUrl;

  const isLoadingAll =
    isLoadingModeration || isLoadingRotation || isLoadingDataByClassId;

  const updateCorrectionNumber = () => {
    setCorrectionNumber((prev) => {
      let newValue: { correctionElNb?: number; correctionDmgNb?: number };
      if (showElement) {
        // Correcting elements: increment element count, retain damage count
        newValue = {
          correctionElNb:
            (prev.correctionElNb || dataCar?.correctedElements || 0) + 1,
          correctionDmgNb:
            prev.correctionDmgNb || dataCar?.correctedDamages || 0,
        };
      } else {
        // Correcting damages: increment damage count, retain element count
        newValue = {
          correctionElNb:
            prev.correctionElNb || dataCar?.correctedElements || 0,
          correctionDmgNb:
            (prev.correctionDmgNb || dataCar?.correctedDamages || 0) + 1,
        };
      }
      // Keep ref in sync
      correctionNumberRef.current = newValue;
      return newValue;
    });
  };

  const confirmAddElement = () => {
    updateCorrectionNumber();
    setSendLogs(true);
  };

  const confirmDeleteElement = useCallback(() => {
    if (showElement) {
      const actualElement = [...listElement];
      const actualAnnotation = [...dataAnnotation];
      setListElement(
        actualElement.filter((_el, index) => index !== currentElement['index']),
      );
      setDataAnnotation(
        actualAnnotation.filter(
          (_annotation, index) => index !== currentElement['index'],
        ),
      );
      setReportEngineData((prev) => ({
        ...prev,
        remove: [
          ...prev.remove,
          {
            ...initReport(currentId),
            isCorrectElements: false,
            elmtCorrectionTypes: CORRECTION_TYPE.SUPPRESSION,
            elmtBeforeCorrection: currentElement['elNumber']?.toString(),
          },
        ],
      }));
    }
    if (showDamage) {
      const actualDamage = [...listDamage];
      const actualAnnotation = [...dataAnnotationDamage];
      setListDamage(
        actualDamage.filter((_el, index) => index !== currentElement['index']),
      );
      setDataAnnotationDamage(
        actualAnnotation.filter(
          (_annotation, index) => index !== currentElement['index'],
        ),
      );

      setReportEngineData((prev) => ({
        ...prev,
        remove: [
          ...prev.remove,
          {
            ...initReport(currentId),
            isCorrectDamages: false,
            dmgCorrectionTypes: CORRECTION_TYPE.SUPPRESSION,
            dmgBeforeCorrection: currentElement['elNumber'].toString(),
          },
        ],
      }));
    }

    // Clear transformation refs to prevent deleted elements from being restored during rotation
    transformedAnnotationRef.current = [];
    transformedDamageAnnotationRef.current = [];
    transformedListElementRef.current = [];
    transformedListDamageRef.current = [];

    updateCorrectionNumber();
    setIsdDeleted(true);
    setSendLogs(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentElement]);

  const onEditElement = (newValue: string) => {
    if (showElement) {
      const actualElement = [...listElement];
      const actualAnnotation = [...dataAnnotation];
      const elementUpdated = actualElement.map((el, index) =>
        index === currentElement['index']
          ? {
              elNumber: newValue,
              color: getColorByValue(
                newValue,
                showElement,
                equipmentsSelectOptions,
                allDamagesSelectOptions,
              ),
            }
          : { ...el },
      );
      const annotationUpdate = actualAnnotation.map((el, index) =>
        index === currentElement['index']
          ? {
              ...el,
              elNumber: newValue,
            }
          : { ...el },
      );
      setListElement([...elementUpdated]);
      setDataAnnotation([...annotationUpdate]);
    }
    if (showDamage) {
      const actualDamage = [...listDamage];
      const actualDmgAnnotation = [...dataAnnotationDamage];
      const dmgUpdated = actualDamage.map((el, index) =>
        index === currentElement['index']
          ? {
              elNumber: newValue,
              color: getColorByValue(
                newValue,
                showElement,
                equipmentsSelectOptions,
                allDamagesSelectOptions,
              ),
            }
          : { ...el },
      );
      const annotationUpdate = actualDmgAnnotation.map((el, index) =>
        index === currentElement['index']
          ? { ...el, elNumber: newValue }
          : { ...el },
      );
      setListDamage([...dmgUpdated]);
      setDataAnnotationDamage([...annotationUpdate]);
    }

    // Clear transformation refs to prevent stale data from being restored during rotation
    transformedAnnotationRef.current = [];
    transformedDamageAnnotationRef.current = [];
    transformedListElementRef.current = [];
    transformedListDamageRef.current = [];

    updateCorrectionNumber();
    setSendLogs(true);
  };

  const getEquipmentsByClassId = async (
    moderatedAnnotations: { elNumber: number }[],
  ) => {
    try {
      if (moderatedAnnotations?.length === 0) return [];

      setIsLoadingDataByClassId(true);

      const allClassIds = moderatedAnnotations?.map((a) => Number(a?.elNumber));
      const uniqueClassIds = [...new Set(allClassIds)];

      const nameMap = new Map<number, string>();
      await Promise.all(
        uniqueClassIds?.map(async (classId) => {
          try {
            const response = await getEquipmentByClassId(classId);
            nameMap?.set(classId, response.data?.name || '');
          } catch (error) {
            console.error(
              'Error fetching equipment name for classId:',
              classId,
              error,
            );
            nameMap?.set(classId, '');
          }
        }),
      );

      return allClassIds
        ?.map((classId) => nameMap.get(classId) || '')
        ?.filter(Boolean);
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setIsLoadingDataByClassId(false);
    }
  };

  const getDamagesByClassId = async (
    moderatedAnnotations: { elNumber: number }[],
  ) => {
    try {
      if (moderatedAnnotations?.length === 0) return [];

      setIsLoadingDataByClassId(true);

      const allClassIds = moderatedAnnotations?.map((a) => Number(a?.elNumber));
      const uniqueClassIds = [...new Set(allClassIds)];

      const nameMap = new Map<number, string>();
      await Promise.all(
        uniqueClassIds?.map(async (classId) => {
          try {
            const response = await getDamageByClassId(classId);
            nameMap?.set(classId, response.data?.name || '');
          } catch (error) {
            console.error(
              'Error fetching damage name for classId:',
              classId,
              error,
            );
            nameMap?.set(classId, '');
          }
        }),
      );

      return allClassIds
        ?.map((classId) => nameMap.get(classId) || '')
        ?.filter(Boolean);
    } catch (error) {
      console.error(error);
      return [];
    } finally {
      setIsLoadingDataByClassId(false);
    }
  };

  const getEquipmentModeratedAnnotation = () => {
    if (!dataAnnotation?.length) return [];

    const originalDetections = dataCar?.result?.detectionsElements || [];
    const originalDetectionsCount = originalDetections.length;

    // Filter to only include annotations that were modified or newly added
    return dataAnnotation
      .map((annotation, index) => {
        // Check if this is an original detection or a new addition
        if (index < originalDetectionsCount) {
          const originalDetection = originalDetections[index];
          // Check if this detection was modified
          const isModified =
            String(annotation.elNumber) !== String(originalDetection?.elNumber);

          // Only include if modified
          if (!isModified) return null;
        }
        // Include modified detections and new additions
        return {
          ...annotation,
          elNumber: Number(annotation.elNumber),
        };
      })
      .filter((annotation) => annotation !== null);
  };

  const getDamagesModeratedAnnotation = () => {
    if (!dataAnnotationDamage?.length) return [];

    const originalDamageDetections =
      dataCar?.result?.detectionsDamagesDetected || [];
    const originalDamageDetectionsCount = originalDamageDetections.length;

    // Filter to only include damage annotations that were modified or newly added
    return dataAnnotationDamage
      .map((annotation, index) => {
        // Check if this is an original detection or a new addition
        if (index < originalDamageDetectionsCount) {
          const originalDetection = originalDamageDetections[index];
          // Check if this damage detection was modified
          const isModified =
            String(annotation.elNumber) !== String(originalDetection?.elNumber);

          // Only include if modified
          if (!isModified) return null;
        }
        // Include modified detections and new additions
        return {
          ...annotation,
          elNumber: Number(annotation.elNumber),
        };
      })
      .filter((annotation) => annotation !== null);
  };

  const getDetectionsElements = () => {
    if (!dataCar?.result?.detectionsElements) return [];

    return dataCar.result.detectionsElements.map((detection, index) => {
      const currentAnnotation = dataAnnotation[index];

      // Check if this detection was moderated (changed from original)
      // A detection is considered moderated if:
      // 1. It was deleted (no corresponding annotation at this index)
      // 2. Its elNumber was changed
      const isModerated = currentAnnotation
        ? String(currentAnnotation.elNumber)?.trim() !==
          String(detection.elNumber)?.trim()
        : true; // If no corresponding annotation, it was deleted

      return {
        ...detection,
        elNumber: Number(detection.elNumber),
        moderated: isModerated,
      };
    });
  };

  const getDetectionsDamages = () => {
    if (!dataCar?.result?.detectionsDamagesDetected) return [];

    return dataCar.result.detectionsDamagesDetected.map((detection, index) => {
      const currentAnnotation = dataAnnotationDamage[index];

      // Check if this damage detection was moderated (changed from original)
      // A detection is considered moderated if:
      // 1. It was deleted (no corresponding annotation at this index)
      // 2. Its elNumber was changed (damages don't have side data)
      const isModerated = currentAnnotation
        ? String(currentAnnotation.elNumber)?.trim() !==
          String(detection.elNumber)?.trim()
        : true; // If no corresponding annotation, it was deleted

      return {
        ...detection,
        elNumber: Number(detection.elNumber),
        moderated: isModerated,
      };
    });
  };

  const validModeration = useCallback(async () => {
    if (!dataAnnotation && !dataAnnotationDamage) return;
    const fileName = dataCar.fileName;
    const fileData: FileData = {
      elementAnnotation: null,
      dmgAnnotation: null,
      image: null,
    };

    const imgParamToUpload = dataCar?.imageUrl?.includes('&token=')
      ? `&uuid=${crypto.randomUUID()}`
      : `?uuid=${crypto.randomUUID()}`;

    const imageToUpload = await createFileFromUrl(
      `${dataCar.imageUrl}${imgParamToUpload}`,
      fileName,
    );

    // Get detected annotations (from original AI detection)
    const detectionsElements = getDetectionsElements();
    const detectionsDamagesDetected = getDetectionsDamages();

    // Get moderated annotations first (after human moderation)
    const moderationsElements = getEquipmentModeratedAnnotation();
    const moderationsDamages = getDamagesModeratedAnnotation();

    // Derive class names from the moderated annotations
    const moderatedClassesElements =
      await getEquipmentsByClassId(moderationsElements);
    const moderatedClassesDamages =
      await getDamagesByClassId(moderationsDamages);

    updateProcess({
      id: currentId,
      correctionElNb: correctionNumberRef.current.correctionElNb,
      correctionDmgNb: correctionNumberRef.current.correctionDmgNb,
      detectionsElements,
      detectionsDamagesDetected,
      moderatedClassesElements,
      moderationsElements,
      moderatedClassesDamages,
      moderationsDamages,
    });

    if (dataAnnotation.length > 0) {
      // Use polygon format when withOldElementRectangles is false/null/undefined
      const usePolygonFormat = !dataCar?.withOldElementRectangles;
      const fileElementUpload = getFileToUpload(
        dataAnnotation,
        dataCar,
        usePolygonFormat,
      );
      fileData.elementAnnotation = fileElementUpload;
      fileData.image = imageToUpload;
    }

    if (dataAnnotationDamage.length > 0) {
      const fileDamageUpload = getFileToUpload(
        dataAnnotationDamage,
        dataCar,
        true,
      );
      fileData.dmgAnnotation = fileDamageUpload;
      fileData.image = imageToUpload;
    }

    if (dataAnnotation.length > 0 || dataAnnotationDamage.length > 0)
      uploadFileToSFTP({ fileData });

    generateReportData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isPendingUploadFileToSFTP,
    dataCar,
    dataAnnotation,
    dataAnnotationDamage,
  ]);

  const validStop = () => {
    engineContinueRef.current = false;
    validModeration();
  };

  const noValidStop = () => {
    setListElement([]);
    setListDamage([]);
    setDataAnnotation([]);
    setDataAnnotationDamage([]);
    navigate('/moteur-de-correction-liste');
  };

  // Initialize correction number ref with dataCar values
  useEffect(() => {
    if (dataCar) {
      correctionNumberRef.current = {
        correctionElNb:
          correctionNumber.correctionElNb || dataCar?.correctedElements || 0,
        correctionDmgNb:
          correctionNumber.correctionDmgNb || dataCar?.correctedDamages || 0,
      };
    }
  }, [dataCar, correctionNumber]);

  // Clear transformation refs whenever state changes to prevent stale data restoration
  useEffect(() => {
    // Only clear if not currently in the middle of a rotation
    if (!isLoadingRotation) {
      transformedAnnotationRef.current = [];
      transformedDamageAnnotationRef.current = [];
      transformedListElementRef.current = [];
      transformedListDamageRef.current = [];
    }
  }, [
    listElement,
    listDamage,
    dataAnnotation,
    dataAnnotationDamage,
    isLoadingRotation,
  ]);

  // Restore transformed coordinates after image rotation
  useEffect(() => {
    if (
      dataCar &&
      dataCar.imageUrl &&
      (transformedAnnotationRef.current.length > 0 ||
        transformedDamageAnnotationRef.current.length > 0 ||
        transformedListElementRef.current.length > 0 ||
        transformedListDamageRef.current.length > 0)
    ) {
      if (transformedAnnotationRef.current.length > 0) {
        setDataAnnotation([...transformedAnnotationRef.current]);
      }
      if (transformedDamageAnnotationRef.current.length > 0) {
        setDataAnnotationDamage([...transformedDamageAnnotationRef.current]);
      }
      if (transformedListElementRef.current.length > 0) {
        setListElement([...transformedListElementRef.current]);
      }
      if (transformedListDamageRef.current.length > 0) {
        setListDamage([...transformedListDamageRef.current]);
      }

      // Clear the refs after restoring
      transformedAnnotationRef.current = [];
      transformedDamageAnnotationRef.current = [];
      transformedListElementRef.current = [];
      transformedListDamageRef.current = [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCar?.imageUrl]);

  // Cleanup temporary URL on unmount or when dataCar changes
  useEffect(() => {
    return () => {
      if (temporaryImageUrl) {
        URL.revokeObjectURL(temporaryImageUrl);
      }
    };
  }, [temporaryImageUrl]);

  // Clear temporary URL when dataCar changes
  // useEffect(() => {
  //   if (temporaryImageUrl) {
  //     URL.revokeObjectURL(temporaryImageUrl);
  //     setTemporaryImageUrl(null);
  //   }
  // }, [dataCar?.id]);

  useEffect(() => {
    if (isLoadingRotatedUrl) {
      setIsLoadingRotatedUrl(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataCar]);

  // Sync rectangle-based state when withOldElementRectangles is true
  useEffect(() => {
    if (dataCar?.withOldElementRectangles) {
      setListElementWithRectangles(listElement);
      setDataAnnotationWithRectangles(dataAnnotation);
    }
  }, [dataCar?.withOldElementRectangles, listElement, dataAnnotation]);

  const skippedModeration = () => {
    const nextId = carProcessedNext?.data?.id;
    // Clear state and navigate to next process without updating status
    setListElement([]);
    setListDamage([]);
    setDataAnnotation([]);
    setDataAnnotationDamage([]);
    setTemporaryImageUrl(carProcessedNext?.data?.imageUrl);
    navigate(`/engine-correction/${nextId}`);
  };

  const validateAndContinue = useCallback(async () => {
    engineContinueRef.current = true;
    validModeration();
  }, [validModeration]);

  const handleValidateModerationByUser = () => {
    const newValue = moderationVerifiedByUserId ? null : loggedInUser?.id;

    setModerationVerifiedByUserId(newValue ?? undefined);

    updateModerationValidation({
      id: currentId,
      moderationVerifiedByUserId: newValue,
    });
  };

  const moderatorName = `${dataCar?.moderatorUser?.firstname || ''} ${
    dataCar?.moderatorUser?.lastname?.toUpperCase() || ''
  }`;

  const moderationVerifierName = `${
    dataCar?.moderationVerifiedBy?.firstname || ''
  } ${dataCar?.moderationVerifiedBy?.lastname?.toUpperCase() || ''}`;

  useEffect(() => {
    if (dataCar?.moderationVerifiedByUserId) {
      setModerationVerifiedByUserId(dataCar?.moderationVerifiedByUserId);
    }
  }, [dataCar]);

  const rotateImage = async (
    imageUrl: string,
    angle: number,
  ): Promise<Blob> => {
    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) {
          resolve(new Blob());
          return;
        }

        // Calculate canvas dimensions based on rotation
        const radians = (angle * Math.PI) / 180;
        const sin = Math.abs(Math.sin(radians));
        const cos = Math.abs(Math.cos(radians));

        const newWidth = img.width * cos + img.height * sin;
        const newHeight = img.width * sin + img.height * cos;

        canvas.width = newWidth;
        canvas.height = newHeight;

        // Move to center, rotate, and draw
        ctx.translate(newWidth / 2, newHeight / 2);
        ctx.rotate(radians);
        ctx.drawImage(img, -img.width / 2, -img.height / 2);

        canvas.toBlob(
          (blob) => {
            resolve(blob || new Blob());
          },
          'image/jpeg',
          0.9,
        );
      };
      img.src = addImageCacheBuster(imageUrl);
    });
  };

  const transformCoordinatesForRotation = (
    coordinates: TResultImageCoordinatesSchema[],
    rotationAngle: number,
  ): TResultImageCoordinatesSchema[] => {
    return coordinates.map((coord) => {
      const transformedCoord = { ...coord };

      // Transform coordinates based on rotation angle
      // Note: coordinates are normalized (0-1) relative to image dimensions
      switch (rotationAngle) {
        case 90:
          // 90° clockwise rotation
          if (
            coord.xCenter !== undefined &&
            coord.yCenter !== undefined &&
            coord.xCenter !== null &&
            coord.yCenter !== null
          ) {
            transformedCoord.xCenter = coord.yCenter;
            transformedCoord.yCenter = 1 - coord.xCenter;
          }
          if (
            coord.width !== undefined &&
            coord.height !== undefined &&
            coord.width !== null &&
            coord.height !== null
          ) {
            // Swap width and height for 90° rotation
            transformedCoord.width = coord.height;
            transformedCoord.height = coord.width;
          }
          // Transform polygon points if they exist
          if (coord.point && coord.point.length > 0) {
            transformedCoord.point = coord.point.map((point) => ({
              x: point.y !== undefined && point.y !== null ? point.y : 0,
              y: point.x !== undefined && point.x !== null ? 1 - point.x : 0,
            }));
          }
          break;
        case 180:
          // 180° rotation
          if (
            coord.xCenter !== undefined &&
            coord.yCenter !== undefined &&
            coord.xCenter !== null &&
            coord.yCenter !== null
          ) {
            transformedCoord.xCenter = 1 - coord.xCenter;
            transformedCoord.yCenter = 1 - coord.yCenter;
          }
          // Width and height remain the same for 180° rotation
          // Transform polygon points if they exist
          if (coord.point && coord.point.length > 0) {
            transformedCoord.point = coord.point.map((point) => ({
              x: point.x !== undefined && point.x !== null ? 1 - point.x : 0,
              y: point.y !== undefined && point.y !== null ? 1 - point.y : 0,
            }));
          }
          break;
        // case 270:
        //   // 270° clockwise rotation (or 90° counter-clockwise)
        //   if (
        //     coord.xCenter !== undefined &&
        //     coord.yCenter !== undefined &&
        //     coord.xCenter !== null &&
        //     coord.yCenter !== null
        //   ) {
        //     transformedCoord.xCenter = coord.yCenter;
        //     transformedCoord.yCenter = 1 - coord.xCenter;
        //   }
        //   if (
        //     coord.width !== undefined &&
        //     coord.height !== undefined &&
        //     coord.width !== null &&
        //     coord.height !== null
        //   ) {
        //     // Swap width and height for 270° rotation
        //     transformedCoord.width = coord.height;
        //     transformedCoord.height = coord.width;
        //   }
        //   // Transform polygon points if they exist
        //   if (coord.point && coord.point.length > 0) {
        //     transformedCoord.point = coord.point.map((point) => ({
        //       x: point.y !== undefined && point.y !== null ? point.y : 0,
        //       y:
        //         point.x !== undefined && point.x !== null
        //           ? 1 - point.x
        //           : point.y !== undefined && point.y !== null
        //           ? point.y
        //           : 0,
        //     }));
        //   }
        //   break;
        default:
          // No transformation needed for 0° or invalid angles
          break;
      }

      return transformedCoord;
    });
  };

  const handleRotateImage = async () => {
    if (!dataCar?.imageUrl) return;

    // Increment rotation count
    rotationCountRef.current++;

    let newRotationAngle = (rotationAngle + 90) % 180;

    // Skip 0° rotation as it doesn't actually change the image
    if (newRotationAngle === 0) {
      newRotationAngle = 90;
    }

    setRotationAngle(newRotationAngle);

    try {
      // Transform coordinates for both equipment and damage annotations
      const transformedDataAnnotation = transformCoordinatesForRotation(
        dataAnnotation,
        newRotationAngle,
      );

      const transformedDataAnnotationDamage = transformCoordinatesForRotation(
        dataAnnotationDamage,
        newRotationAngle,
      );

      // Store transformed coordinates and lists in refs to be restored when new image is ready
      transformedAnnotationRef.current = transformedDataAnnotation;
      transformedDamageAnnotationRef.current = transformedDataAnnotationDamage;
      transformedListElementRef.current = [...listElement];
      transformedListDamageRef.current = [...listDamage];

      // Note: Don't update state here - let the useEffect handle it when the new image URL is set
      // This prevents duplicate annotations from accumulating

      // Determine the current image URL (could be a rotated image from previous rotation)
      const currentImageUrl = newImageUrlRef.current || dataCar.imageUrl;

      const rotatedBlob = await rotateImage(currentImageUrl, newRotationAngle);

      // Create and set temporary URL to show the blob immediately
      const tempUrl = URL.createObjectURL(rotatedBlob);
      setTemporaryImageUrl(tempUrl);
      setIsLoadingRotatedUrl(true);

      const imageName = currentImageUrl.includes(FIREBASE_BASE_URL)
        ? `${crypto.randomUUID()}.jpg`
        : currentImageUrl?.split('/').pop();

      newImageUrlRef.current = `${R2_PUBLIC_DOMAIN}/moderations/images/${imageName}`;

      // Create a File object from the blob
      const rotatedFile = new File([rotatedBlob], imageName, {
        type: 'image/jpeg',
      });

      uploadRotatedImage([rotatedFile]);
    } catch (error) {
      console.error('Error rotating image:', error);
    }
  };

  return (
    <>
      <Card
        title={
          <div className="w-full flex flex-wrap items-center gap-5">
            <div className="flex flex-1 max-sm:basis-full max-sm:w-full">
              <TitleSection
                text={
                  <div className="flex items-center gap-2">
                    {processingId && (
                      <>
                        <BackButton
                          buttonClassName="w-7 min-w-7 h-7 min-h-7 bg-purple-A713F6"
                          disabled={isLoadingAll}
                          onClick={() => {
                            navigate(backToLink);
                          }}
                        />
                        <div className="text-md font-thin">
                          {t('common:back')}
                        </div>
                        <div className="ml-0.5 mt-[1px] text-2sm text-slate-900 font-normal">
                          |
                        </div>
                      </>
                    )}
                    <div>
                      {processingId
                        ? disableModeration
                          ? tab === EditEquipmentTab.DETECTIONS
                            ? t('common:detections')
                            : t('common:moderations')
                          : t('moderateThisElement')
                        : t('moderateThisElement')}
                    </div>
                  </div>
                }
                defaultIconClassName={processingId ? '' : 'ki-toggle-on'}
              />
            </div>
            {disableModeration ? (
              // <div className="h-[32px]" />
              <></>
            ) : (
              <div className="flex items-center gap-3 max-sm:w-full max-sm:justify-center">
                {dataCar?.status === HumanModerationStatus.MODERE && (
                  <>
                    {!isNotOnModerationScreen && isAdmin ? (
                      <div>
                        <Switch
                          rightText={
                            !isPendingUpdateModerationValidation &&
                            moderationVerifiedByUserId
                              ? `${t(
                                  'common:verifiedBy',
                                )} ${moderationVerifierName}`
                              : t('validateVerification')
                          }
                          triggerClassName="px-4 py-2.25 rounded-full border border-dashed border-purple-4104E1/40 shadow-sm"
                          rightTextClassName="-ml-0.5 mt-0.5 text-primary text-2sm font-medium"
                          disabled={isPendingUpdateModerationValidation}
                          checked={!!moderationVerifiedByUserId}
                          onChange={handleValidateModerationByUser}
                        />
                      </div>
                    ) : (
                      <ValidationTag
                        text={
                          isNotOnModerationScreen && !moderationVerifiedByUserId
                            ? t('moderationToVerify')
                            : moderationVerifiedByUserId
                              ? `${t(
                                  'common:verifiedBy',
                                )} ${moderationVerifierName}`
                              : t('common:toVerify')
                        }
                        className={twMerge(
                          '',
                          isNotOnModerationScreen &&
                            !moderationVerifiedByUserId &&
                            'border-red-FF2929 text-red-FF2929',
                        )}
                      />
                    )}
                    <div className="mx-1.5 mt-0.5 text-slate-300 text-xl font-thin max-moderation-screen:hidden">
                      |
                    </div>
                  </>
                )}
                <div className="flex items-center gap-1.5 max-moderation-screen:hidden">
                  <div
                    className={twMerge(
                      'flex justify-center items-center bg-primary rounded-full p-2 shadow-lg hover:cursor-pointer',
                      isLoadingAll && 'pointer-events-none opacity-80',
                    )}
                    onClick={handleRotateImage}
                  >
                    <RotationIcon className="w-4 min-w-4 h-4 min-h-4 [&>path]:fill-white scale-x-[-1]" />
                  </div>
                  <div className="text-2sm font-normal text-primary hover:cursor-default">
                    {t('changeImageRotation')}
                  </div>
                </div>
              </div>
            )}
          </div>
        }
        bodyClassName={twMerge(
          'pt-6 pb-10',
          isLoadingAll && 'pointer-events-none',
        )}
      >
        <div>
          {dataCar?.moderatorUserId && (
            <div className="text-2sm text-gray-600 font-medium mb-2.5">
              {`${t('common:moderatedBy')} ${moderatorName}`}
            </div>
          )}
          {/* {disableModeration ? (
            // <div className="-mt-[7.8px] mb-5">
            <div className="mb-5">
              <NotFound
                containerClassName="gap-1.75 border bg-pink-F20089/90 px-2.75 pt-[13px] pb-[10px] rounded"
                textClassName="text-sm text-white font-normal"
                icon={
                  <i className="-mt-[5.2px] ki-outline ki-information-2 text-xl text-white font-thin" />
                }
                text={
                  processingId
                    ? t('elementModerationNotAllowed')
                    : t('youCanOnlyModerateDamages')
                }
              />
            </div>
          ) : ( */}
          {isNotOnModerationScreen ? (
            <div className="mb-4">
              <NotFound
                containerClassName="gap-1.75 border bg-red-FF2929/95 px-2.75 pt-[13px] pb-[10.5px] rounded"
                textClassName="text-sm text-white leading-tight max-sm:text-2sm"
                icon={
                  <i className="-mt-1.5 ki-outline ki-information-2 text-xl text-white font-semibold" />
                }
                text={
                  isAdmin && !moderationVerifiedByUserId
                    ? t('engineCorrectionWarningMessageForVerifier')
                    : t('engineCorrectionWarningMessageForModerator')
                }
              />
            </div>
          ) : (
            <>
              <div className="inline-flex gap-3 mb-5 max-moderation-screen:hidden">
                {processingId ? (
                  <button
                    className={twMerge(
                      'btn btn-success pt-0.5',
                      isLoadingAll && 'disabled',
                    )}
                    onClick={validStop}
                  >
                    <IconCircleDashedCheck stroke={2} />
                    {isLoadingAll
                      ? t('common:inProgress')
                      : processingId
                        ? t('common:validate')
                        : t('validateAndStop')}
                  </button>
                ) : (
                  <>
                    <button
                      className={twMerge(
                        'btn btn-outline btn-success pt-0.5',
                        isLoadingAll && 'disabled',
                      )}
                      onClick={validateAndContinue}
                    >
                      <IconCircleDashedCheck stroke={2} />
                      {isLoadingAll
                        ? t('common:inProgress')
                        : t('validateAndContinueModeration')}
                    </button>
                    <button
                      className={twMerge(
                        'btn btn-outline btn-success pt-0.5',
                        isLoadingAll && 'disabled',
                      )}
                      onClick={validStop}
                    >
                      <IconCircleDashedCheck stroke={2} />
                      {isLoadingAll
                        ? t('common:inProgress')
                        : t('validateAndStop')}
                    </button>

                    <button
                      className={twMerge(
                        'btn btn-outline btn-danger pt-0.5',
                        isLoadingAll && 'disabled',
                      )}
                      onClick={noValidStop}
                    >
                      <IconHandStop stroke={2} />
                      {isLoadingAll
                        ? t('common:inProgress')
                        : t('stopWithoutValidation')}
                    </button>

                    <button
                      className={twMerge(
                        'btn btn-outline btn-primary pt-0.5',
                        isLoadingAll && 'disabled',
                      )}
                      onClick={skippedModeration}
                    >
                      <IconPlayerTrackNext stroke={2} />
                      {isLoadingAll ? t('common:inProgress') : t('common:skip')}
                    </button>
                  </>
                )}
              </div>
            </>
          )}
          <div className="flex gap-x-[21px]">
            <div
              className={twMerge(
                'min-w-[868.1px] max-w-[868.1px] relative max-moderation-screen:min-w-0 max-moderation-screen:max-w-none max-moderation-screen:after:content-[""] max-moderation-screen:after:absolute max-moderation-screen:after:block max-moderation-screen:after:w-full max-moderation-screen:after:h-full max-moderation-screen:after:top-0 max-moderation-screen:after:left-0 max-moderation-screen:after:hover:cursor-not-allowed',
                isLoadingModeration && 'bg-blue-131025',
              )}
            >
              {isLoadingRotation ? (
                <ModerationReportCanvas
                  t={t}
                  imgSrc={temporaryImageUrl}
                  mode={MODE_DRAW.POLYGON}
                />
              ) : (
                <Canvas
                  t={t}
                  imageSrc={temporaryImageUrl || stableImageUrl}
                  listAnnotation={
                    showElement ? dataAnnotation : dataAnnotationDamage
                  }
                  showElement={showElement}
                  equipmentClassId={equipmentClassId}
                  confirmAddElement={confirmAddElement}
                  mode={
                    showElement
                      ? dataCar?.withOldElementRectangles
                        ? MODE_DRAW.RECTANGLE
                        : MODE_DRAW.POLYGON
                      : MODE_DRAW.POLYGON
                  }
                  disableDraw={disableModeration}
                />
              )}
            </div>
            <div
              className={twMerge(
                'w-[280.9px] max-moderation-screen:hidden',
                isLoadingAll && 'opacity-80',
              )}
            >
              <div className="font-semibold text-center pb-3 mb-3 relative after:content-[''] after:absolute after:bottom-0 after:left-0 after:w-full after:h-[1px] after:border-b after:border-dashed after:border-b-slate-200">
                {showOnlyDetections
                  ? t('detectionList')
                  : showOnlyModerations
                    ? t('moderationList')
                    : t('detectionOrModerationList')}
              </div>
              {!(showOnlyEquipments || showOnlyDamages) && (
                <TabList className="flex-nowrap gap-3 pb-3 max-[1599px]:grid max-[1599px]:grid-cols-1">
                  <TabItem
                    className={twMerge('', !showElement && 'border-black/50')}
                    text={t('common:equipments')}
                    leftIcon={<i className="ki-outline ki-size" />}
                    active={showElement}
                    onChange={() => {
                      setShowElement(true);
                      setShowDamage(false);
                    }}
                  />
                  <TabItem
                    className={twMerge('', !showDamage && 'border-black/50')}
                    text={t('common:damages')}
                    leftIcon={<i className="ki-outline ki-bucket" />}
                    active={showDamage}
                    onChange={() => {
                      setShowElement(false);
                      setShowDamage(true);
                    }}
                  />
                </TabList>
              )}
              <div id="tab_element-list">
                <ListElementCar
                  t={t}
                  isLoading={isLoadingModeration}
                  disableModeration={disableModeration}
                  listElement={showElement ? listElement : listDamage}
                  showElement={showElement}
                  showOnlyEquipments={showOnlyEquipments}
                  showOnlyDamages={showOnlyDamages}
                  showOnlyDetections={
                    showOnlyDetections || showOnlyModerations === false
                  }
                  equipmentClassId={equipmentClassId}
                  onConfirmModal={confirmDeleteElement}
                  onEdit={onEditElement}
                />
              </div>
            </div>
          </div>
        </div>
      </Card>
      {isLoadingAll && <Loader />}
    </>
  );
};
